/**
 * Coverage-summary-klient för Lyktan (S-04-07).
 *
 * Wrappar `GET /api/admin/hearths/coverage-summary` (backend, S-04-07).
 * Backend tillämpar K=5+l=2-trösklarna: items med suppressed=true
 * saknar aggregat-fält och ska visas som "för få walkare" i UI.
 *
 * Hard-rules:
 *  - Cookie-baserad auth via `credentials: 'include'`. Inga tokens i body
 *    eller localStorage/sessionStorage.
 *  - Inga walk-id, user-id eller per-walker-uppgifter — backend levererar
 *    bara aggregat per härd.
 *  - GET-endpoints är inte CSRF-skyddade på backend (backend använder
 *    SameSite=Strict-cookies + endast mutating-CSRF), så ingen
 *    CSRF-header behövs här.
 */

export type CoverageSummaryItem = {
  hearthId: string;
  name: string;
  priority: 'low' | 'medium' | 'high';
  suppressed: boolean;
  /** Antal unika walkare. Null när suppressed=true. */
  distinctWalkers: number | null;
  /** Antal passager (rader i hearth_coverage). Null när suppressed=true. */
  passageCount: number | null;
  /** Total täckt distans i meter. Null när suppressed=true. */
  totalDistanceM: number | null;
  /** Total tid inom härd i sekunder. Null när suppressed=true. */
  totalDurationS: number | null;
  /** Opaque kod när suppressed=true: 'for_few_walkers', 'for_few_days' eller 'for_few_walkers_and_days'. */
  suppressionReason:
    | 'for_few_walkers'
    | 'for_few_days'
    | 'for_few_walkers_and_days'
    | null;
};

export type CoverageSummary = {
  items: CoverageSummaryItem[];
  /** k-tröskel som backend tillämpar — exponeras för UI-text. */
  kThreshold: number;
  /** l-tröskel som backend tillämpar — exponeras för UI-text. */
  lThreshold: number;
  /**
   * Antal rader som inte kunde tolkas (okänd enum, saknade fält) och
   * därför inte renderas. Robusthets-fix (arkitektur-svep C3): en
   * trasig rad ska inte fälla hela vyn — giltiga rader visas och UI:t
   * visar en varning med antalet. 0 = allt tolkades.
   */
  invalidItemCount: number;
};

export type CoverageSummarySuccess = { kind: 'ok'; summary: CoverageSummary };
export type CoverageSummaryFailure = { kind: 'failure'; message: string };
export type CoverageSummaryResult =
  | CoverageSummarySuccess
  | CoverageSummaryFailure;

const API_PATH = '/api/admin/hearths/coverage-summary';

function messageForError(code: string): string {
  switch (code) {
    case 'unauthorized':
      return 'Sessionen har gått ut. Logga in igen.';
    case 'forbidden':
      return 'Saknar behörighet att se täckningsöversikten.';
    case 'network':
      return 'Kunde inte nå servern. Kontrollera anslutningen.';
    default:
      return 'Något gick fel när täckningsöversikten skulle laddas. Försök igen om en stund.';
  }
}

type RawItem = {
  hearth_id?: unknown;
  name?: unknown;
  priority?: unknown;
  suppressed?: unknown;
  distinct_walkers?: unknown;
  passage_count?: unknown;
  total_distance_m?: unknown;
  total_duration_s?: unknown;
  suppression_reason?: unknown;
};

type RawBody = {
  items?: unknown;
  k_threshold?: unknown;
  l_threshold?: unknown;
};

function isPriority(value: unknown): value is 'low' | 'medium' | 'high' {
  return value === 'low' || value === 'medium' || value === 'high';
}

function isSuppressionReason(
  value: unknown,
): value is CoverageSummaryItem['suppressionReason'] {
  return (
    value === null ||
    value === 'for_few_walkers' ||
    value === 'for_few_days' ||
    value === 'for_few_walkers_and_days'
  );
}

function parseItem(raw: RawItem): CoverageSummaryItem | null {
  if (
    typeof raw.hearth_id !== 'string' ||
    typeof raw.name !== 'string' ||
    !isPriority(raw.priority) ||
    typeof raw.suppressed !== 'boolean'
  ) {
    return null;
  }
  const optionalNumber = (v: unknown): number | null =>
    typeof v === 'number' ? v : null;
  const reasonRaw = raw.suppression_reason ?? null;
  if (!isSuppressionReason(reasonRaw)) return null;
  return {
    hearthId: raw.hearth_id,
    name: raw.name,
    priority: raw.priority,
    suppressed: raw.suppressed,
    distinctWalkers: optionalNumber(raw.distinct_walkers),
    passageCount: optionalNumber(raw.passage_count),
    totalDistanceM: optionalNumber(raw.total_distance_m),
    totalDurationS: optionalNumber(raw.total_duration_s),
    suppressionReason: reasonRaw,
  };
}

export async function fetchCoverageSummary(): Promise<CoverageSummaryResult> {
  let response: Response;
  try {
    response = await fetch(API_PATH, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.status === 401) {
    return { kind: 'failure', message: messageForError('unauthorized') };
  }
  if (response.status === 403) {
    return { kind: 'failure', message: messageForError('forbidden') };
  }
  if (!response.ok) {
    return { kind: 'failure', message: messageForError('default') };
  }

  let body: RawBody;
  try {
    body = (await response.json()) as RawBody;
  } catch {
    return { kind: 'failure', message: messageForError('default') };
  }

  if (
    !Array.isArray(body.items) ||
    typeof body.k_threshold !== 'number' ||
    typeof body.l_threshold !== 'number'
  ) {
    return { kind: 'failure', message: messageForError('default') };
  }

  // Robusthets-fix (arkitektur-svep C3, 2026-07-04): parse per rad.
  // Tidigare fällde EN trasig rad (t.ex. okänd suppression_reason-enum
  // efter backend-utbyggnad) hela vyn till felskärm. Nu samlas ogiltiga
  // rader i en räknare och giltiga rader renderas — admin ser en
  // varning istället för en död vy.
  const items: CoverageSummaryItem[] = [];
  let invalidItemCount = 0;
  for (const raw of body.items) {
    const parsed = parseItem(raw as RawItem);
    if (parsed === null) {
      invalidItemCount += 1;
      continue;
    }
    items.push(parsed);
  }

  return {
    kind: 'ok',
    summary: {
      items,
      kThreshold: body.k_threshold,
      lThreshold: body.l_threshold,
      invalidItemCount,
    },
  };
}

/** Mappar suppression-orsak till svensk användartext. */
export function suppressionMessage(
  reason: CoverageSummaryItem['suppressionReason'],
  kThreshold: number,
  lThreshold: number,
): string {
  switch (reason) {
    case 'for_few_walkers':
      return `För få promenörer (under ${kThreshold}). Aggregat döljs av integritetsskäl.`;
    case 'for_few_days':
      return `För få dagar (under ${lThreshold}). Aggregat döljs av integritetsskäl.`;
    case 'for_few_walkers_and_days':
      return `För få promenörer och dagar. Aggregat döljs av integritetsskäl.`;
    default:
      return '';
  }
}

/** Formaterar meter som "1,2 km" eller "850 m". */
export function formatDistance(meters: number): string {
  if (meters >= 1000) {
    const km = meters / 1000;
    return `${km.toLocaleString('sv-SE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
  }
  return `${meters.toLocaleString('sv-SE')} m`;
}

/** Formaterar sekunder som "12 min" eller "1 h 30 min". */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) {
    return `${hours} h ${minutes} min`;
  }
  return `${minutes} min`;
}
